import { Conversation, ConversationList } from '@chatscope/chat-ui-kit-react';
import { IconButton, useMediaQuery, useTheme } from '@mui/material';
import api from 'api';
import InstructorAccount from 'api/Serializers/Accounts/Instructor';
import Button from 'components/button';
import Checkbox from 'components/checkbox';
import Controls from 'components/controls';
import Loading from 'components/loading';
import Modal from 'components/modal';
import { FETCH_STATE, MESSAGING_PAGE_SIZE, UserType } from 'config';
import useAccountPatch from 'hooks/useAccountPatch';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { SearchIcon, SettingsIcon } from 'icons';
import { MessagingConvoFailedError } from 'lang/en/Snackbars';
import { messagingClientManager } from 'messagingClientManager';
import { useSnackbar } from 'notistack';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import {
  getAccountDetail,
  getActiveConversationSid,
  getConversations,
  getInstructorListFetchState,
  getMessages,
  getMessagingInit,
  getParticipants,
  getUserType,
} from 'state/selectors';
import {
  insertConversationDataSets,
  parseAndInsertMessages,
  sdkConversations,
  setActiveConversationSid,
  upsertParticipants,
} from 'state/slice/messaging';
import { SHARED_ROUTES } from 'utils/routing';
import { MessagingAvatar } from './components';
import CreateConversationModal from './create-conversation';
import { getMessagingDateStr } from './utils';

enum Filter {
  Active = 'booked',
  Past = 'past',
  Leads = 'leads',
}

const ConversationsContainer = (): JSX.Element => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const theme = useTheme();
  // FIXME match chatscope's breakpoints with our own
  const isBreakSm = useMediaQuery(theme.breakpoints.up('sm'));
  const { userId } = useParams<{ userId: string }>();
  const init = useSelector(getMessagingInit);
  const conversations = useSelector(getConversations);
  const messages = useSelector(getMessages);
  const participants = useSelector(getParticipants);
  const activeConvoSid = useSelector(getActiveConversationSid);
  const instructorListFetchState = useSelector(getInstructorListFetchState);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>();

  const filteredConversations = useMemo(() => {
    return conversations.filter((convo) => {
      const participant = participants[convo.sid];
      if (participant === undefined || !convo.lastMessage.dateCreated) {
        return false;
      }
      if (filter === Filter.Active) {
        return !!participant.nextBooking;
      } else if (filter === Filter.Past) {
        return participant.numBookings > 0;
      } else if (filter === Filter.Leads) {
        return participant.numBookings === 0;
      }
      return true;
    });
  }, [filter, conversations, participants]);

  useEffect(() => {
    if (init === undefined) {
      return;
    }
    (async () => {
      if (init) {
        const numId = Number(userId);
        if (!Number.isInteger(numId)) {
          dispatch(setActiveConversationSid(null));
        } else {
          await loadConversation(numId);
        }
      }
      setIsLoading(false); // if init failed, the error modal is triggered higher up
    })();
  }, [init, userId]);

  const createConversation = async (userId: number) => {
    try {
      const response = await api.conversations.create({ userId: userId });
      const conversationSid = response.data.conversationSid;
      const conversation =
        await messagingClientManager.client.getConversationBySid(
          conversationSid
        );
      await dispatch(insertConversationDataSets([conversation], false));
      dispatch(upsertParticipants([response.data]));
      return conversationSid;
    } catch (err) {
      enqueueSnackbar(MessagingConvoFailedError);
      logger.error(`Conversation creation failed: ${err}`, {
        twilioErrorCode: (err as any).body?.code,
        twilioStatusCode: (err as any).body?.status,
      });
      return undefined;
    }
  };

  const fetchFirstConversationMessagesPage = async (
    conversationSid: string
  ) => {
    if (messages[conversationSid] !== undefined) {
      return true;
    }
    let sdkConvo = sdkConversations.get(conversationSid);
    try {
      const paginator = await sdkConvo.getMessages(MESSAGING_PAGE_SIZE);
      dispatch(parseAndInsertMessages(conversationSid, paginator.items));
      return true;
    } catch (err) {
      enqueueSnackbar(MessagingConvoFailedError);
      logger.error(`Fetching messages for conversaion failed: ${err}`, {
        twilioErrorCode: (err as any).body?.code,
        twilioStatusCode: (err as any).body?.status,
      });
      return false;
    }
  };

  const loadConversation = async (userId: number) => {
    setIsLoading(true);
    let conversationSid = Object.values(participants).find(
      (part) => part.userId === userId
    )?.conversationSid;
    if (conversationSid === undefined) {
      conversationSid = await createConversation(userId);
    }
    if (conversationSid !== undefined) {
      const messagesLoaded = await fetchFirstConversationMessagesPage(
        conversationSid
      );
      if (messagesLoaded) {
        dispatch(setActiveConversationSid(conversationSid));
      }
    }
    setIsLoading(false);
  };

  return (
    <>
      {instructorListFetchState === FETCH_STATE.GET && (
        <Loading message="Getting your instructors..." />
      )}
      <ConversationListHeader filter={filter} setFilter={setFilter} />
      {isLoading && <Loading position="absolute" />}
      <ConversationList
        className={isLoading ? 'pointer-events-none opacity-50' : null}
      >
        {filteredConversations.map((conversation) => (
          <Conversation
            id={`sid-${conversation.sid}`}
            key={conversation.sid}
            unreadCnt={conversation.unreadMessageCount}
            active={conversation.sid === activeConvoSid}
            onClick={() =>
              history.push(
                SHARED_ROUTES.MESSAGES.nav(
                  participants[conversation.sid].userId
                )
              )
            }
          >
            <MessagingAvatar
              as="Avatar"
              participant={participants[conversation.sid]}
            />
            <Conversation.Content
              className={!isBreakSm && !activeConvoSid ? '!flex' : ''}
              // TODO: the `capitalize()` incorrectly formats hyphenated last names
              name={participants[conversation.sid].displayName.capitalize()}
              info={
                conversation.lastMessage.dateCreated
                  ? getMessagingDateStr(conversation.lastMessage.dateCreated)
                  : 'No chat history'
              }
            />
          </Conversation>
        ))}
      </ConversationList>
    </>
  );
};

const ConversationListHeader = ({
  filter,
  setFilter,
}: {
  filter: Filter;
  setFilter: React.Dispatch<React.SetStateAction<Filter>>;
}) => {
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const userType = useSelector(getUserType);
  const pillCls = '!h-8 !px-2 !text-sm font-normal';
  const getVariant = (relatedFilter: Filter) =>
    relatedFilter === filter ? 'flat' : 'outlined';
  const getColor = (relatedFilter: Filter) =>
    relatedFilter === filter ? 'primary' : 'default';
  const handleClickFilter = (relatedFilter: Filter) =>
    setFilter((curr) => (curr === relatedFilter ? undefined : relatedFilter));
  return (
    <>
      <div className="flex pt-3 px-[0.675em] pb-2">
        <div className="flex gap-2.5 w-full justify-between items-center">
          <div className="flex gap-2.5 w-full items-center">
            <button
              className="btn-icon border text-blue-500 border-blue-500 hover:bg-blue-200 hover:text-blue-600"
              onClick={() => setIsSearchModalOpen(true)}
            >
              <SearchIcon width={22} />
            </button>
            {userType === UserType.Instructor && (
              <>
                <Button
                  className={pillCls}
                  variant={getVariant(Filter.Active)}
                  onClick={() => handleClickFilter(Filter.Active)}
                  color={getColor(Filter.Active)}
                >
                  Active
                </Button>
                <Button
                  className={pillCls}
                  variant={getVariant(Filter.Past)}
                  onClick={() => handleClickFilter(Filter.Past)}
                  color={getColor(Filter.Past)}
                >
                  Past
                </Button>
                <Button
                  className={pillCls}
                  variant={getVariant(Filter.Leads)}
                  onClick={() => handleClickFilter(Filter.Leads)}
                  color={getColor(Filter.Leads)}
                >
                  Leads
                </Button>
              </>
            )}
          </div>
          {userType === UserType.Instructor && (
            <IconButton
              size="small"
              onClick={() => setIsSettingsModalOpen(true)}
            >
              <SettingsIcon width={22} />
            </IconButton>
          )}
        </div>
      </div>
      {userType === UserType.Instructor && (
        <MessagingSettingsModal
          open={isSettingsModalOpen}
          onClose={() => setIsSettingsModalOpen(false)}
        />
      )}
      <CreateConversationModal
        open={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
      />
    </>
  );
};

const MessagingSettingsModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose(): void;
}) => {
  const account = useSelector(getAccountDetail) as InstructorAccount;
  const { handleAccountUpdate } = useAccountPatch();
  const [isUpdating, setIsUpdating] = useState(false);
  const [allowUnbookedMessaging, setAllowUnbookedMessaging] = useState(
    account.allowUnbookedMessaging
  );

  const handleSave = async () => {
    let succeeded = true;
    if (account.allowUnbookedMessaging !== allowUnbookedMessaging) {
      setIsUpdating(true);
      const result = await handleAccountUpdate({
        allowUnbookedMessaging: allowUnbookedMessaging,
      });
      succeeded = !!result;
    }
    setIsUpdating(false);
    if (succeeded) {
      onClose();
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        name="conversation-settings-modal"
        title="Messaging settings"
        maxWidth="xs"
        fullWidth
      >
        <div className="mb-8">
          <Checkbox
            label="Allow clients to message before booking"
            checked={allowUnbookedMessaging}
            onChange={() => {
              setAllowUnbookedMessaging((prev) => !prev);
            }}
          />
          <p className="mt-2 text-gray-600">
            Instructors that respond to client inquiries are more likely to
            receive bookings.
          </p>
        </div>
        <Controls className="!pb-0">
          <Button variant="contained" onClick={onClose}>
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            isLoading={isUpdating}
            onClick={async () => await handleSave()}
          >
            Save
          </Button>
        </Controls>
      </Modal>
    </>
  );
};

export default ConversationsContainer;
